/**
 * Mapping helpers between the UI's `HH:mm` + weekday-name model and the API's
 * canonical `timeOfDay` (minutes since midnight) + `daysOfWeek` (ISO weekday
 * ints, 1=Mon..7=Sun) representation.
 */

export const WEEKDAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type WeekdayName = (typeof WEEKDAY_NAMES)[number];

/** Weekday name → ISO weekday int (monday=1 .. sunday=7). */
export function weekdayNameToIso(name: WeekdayName): number {
  return WEEKDAY_NAMES.indexOf(name) + 1;
}

/** ISO weekday int (1..7) → weekday name. */
export function isoToWeekdayName(iso: number): WeekdayName | undefined {
  return WEEKDAY_NAMES[iso - 1];
}

/** `HH:mm` → minutes since midnight (e.g. `'09:00'` → 540). */
export function timeStringToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  return (hours || 0) * 60 + (minutes || 0);
}

/** Minutes since midnight → `HH:mm` (e.g. 540 → `'09:00'`). */
export function minutesToTimeString(minutes: number): string {
  const clamped = Math.max(0, Math.min(minutes, 24 * 60 - 1));
  const hours = Math.floor(clamped / 60);
  const mins = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
